import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { API_BASE, formatApiError } from "../../components/AuthContext";
import { toast } from "sonner";
import { Receipt, Loader2, ArrowLeft, CheckCircle2, CreditCard } from "lucide-react";
import { logError } from "../../lib/log";

function fmtMoney(v, ccy = "usd") {
  try { return new Intl.NumberFormat(undefined, { style: "currency", currency: (ccy || "usd").toUpperCase() }).format(Number(v)); }
  catch { return `$${Number(v).toFixed(2)}`; }
}
function fmtDate(iso) { if (!iso) return ""; return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }

export function PortalInvoicesList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API_BASE}/portal/invoices`)
      .then((r) => setRows(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="px-10 py-12 max-w-[1000px]" data-testid="portal-invoices-list">
      <div className="mb-8">
        <div className="overline mb-2 text-[var(--accent)]">Billing</div>
        <h1 className="font-heading text-4xl sm:text-5xl leading-tight mb-2" style={{ fontWeight: 500 }}>
          Your <em className="italic" style={{ color: "var(--accent)" }}>invoices</em>
        </h1>
        <p className="text-[var(--ink-2)] max-w-2xl">
          Every invoice from your accountant, plus payment status. Click any invoice to view details or pay online.
        </p>
      </div>

      {loading ? <div className="text-[var(--ink-3)]">Loading…</div>
        : rows.length === 0 ? (
        <div className="card p-10 text-center" data-testid="portal-invoices-empty">
          <Receipt size={28} className="mx-auto mb-3 text-[var(--ink-3)]" />
          <div className="font-heading text-xl mb-1" style={{ fontWeight: 500 }}>No invoices</div>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="report-table">
            <thead><tr><th>#</th><th>Issued</th><th>Total</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {rows.map((inv) => (
                <tr key={inv.invoice_id} data-testid={`portal-invoice-row-${inv.invoice_id}`}>
                  <td className="font-mono">{inv.number}</td>
                  <td className="text-[13px]">{fmtDate(inv.sent_at || inv.created_at)}</td>
                  <td className="font-mono">{fmtMoney(inv.total, inv.currency)}</td>
                  <td>
                    <span className="badge" style={{
                      background: inv.status === "paid" ? "var(--moss)" : inv.status === "sent" ? "var(--accent)" : "var(--ink-3)",
                      color: "var(--paper)"
                    }}>{inv.status}</span>
                  </td>
                  <td className="text-right">
                    <Link to={`/portal/invoices/${inv.invoice_id}`} className="text-[12px] text-[var(--accent)] hover:underline">View →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function PortalInvoice() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [inv, setInv] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    const r = await axios.get(`${API_BASE}/portal/invoices/${id}`);
    setInv(r.data);
  }, [id]);

  useEffect(() => { reload().catch(console.error); }, [reload]);

  // After Stripe redirect with session_id, poll status
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await axios.get(`${API_BASE}/portal/invoices/${id}/checkout-status/${sid}`);
        if (r.data.invoice_paid) {
          toast.success("Payment received — thank you!");
          await reload();
          return;
        }
      } catch (err) { logError("portal:invoice-checkout-poll", err); }
      if (attempts < 6) setTimeout(poll, 2000);
      else toast.info("Payment processing — refresh in a minute if not updated.");
    };
    poll();
  }, [params, id, reload]);

  const pay = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/portal/invoices/${id}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  if (!inv) return <div className="p-12 text-[var(--ink-3)]">Loading…</div>;

  return (
    <div className="px-10 py-12 max-w-[820px]" data-testid="portal-invoice-page">
      <Link to="/portal/invoices" className="inline-flex items-center gap-1 text-[12px] text-[var(--ink-3)] hover:text-[var(--ink)] mb-4">
        <ArrowLeft size={12} /> Back to invoices
      </Link>

      <div className="card p-8" data-testid="portal-invoice-card">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="overline mb-1">Invoice</div>
            <div className="font-heading text-3xl" style={{ fontWeight: 500 }}>{inv.number}</div>
          </div>
          <span className="badge" style={{
            background: inv.status === "paid" ? "var(--moss)" : inv.status === "sent" ? "var(--accent)" : "var(--ink-3)",
            color: "var(--paper)"
          }}>{inv.status}</span>
        </div>

        <table className="w-full mb-6" data-testid="portal-invoice-lines">
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border)" }}>
              <th className="text-left py-2 text-[11px] uppercase tracking-wider text-[var(--ink-3)]">Description</th>
              <th className="text-right py-2 text-[11px] uppercase tracking-wider text-[var(--ink-3)]">Qty</th>
              <th className="text-right py-2 text-[11px] uppercase tracking-wider text-[var(--ink-3)]">Unit</th>
              <th className="text-right py-2 text-[11px] uppercase tracking-wider text-[var(--ink-3)]">Total</th>
            </tr>
          </thead>
          <tbody>
            {inv.lines.map((l, i) => (
              <tr key={`line-${i}-${l.description}`} style={{ borderBottom: "1px solid var(--border)" }}>
                <td className="py-3 text-[14px]">{l.description}</td>
                <td className="py-3 text-right font-mono">{l.quantity}</td>
                <td className="py-3 text-right font-mono">{fmtMoney(l.unit_amount, inv.currency)}</td>
                <td className="py-3 text-right font-mono">{fmtMoney(l.quantity * l.unit_amount, inv.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-between items-center mb-6">
          <div className="text-[var(--ink-3)] text-[13px]">
            {inv.due_at && <>Due {fmtDate(inv.due_at)}</>}
          </div>
          <div className="text-right">
            <div className="overline text-[10px]">Total</div>
            <div className="font-heading text-3xl tabular" style={{ fontWeight: 500 }}>
              {fmtMoney(inv.total, inv.currency)}
            </div>
          </div>
        </div>

        {inv.notes && (
          <div className="card p-4 mb-6 text-[13px] text-[var(--ink-2)]" style={{ background: "var(--subtle)" }}>
            {inv.notes}
          </div>
        )}

        {inv.status === "paid" ? (
          <div className="card p-5 text-center" data-testid="portal-invoice-paid" style={{ background: "var(--subtle)" }}>
            <CheckCircle2 size={24} className="mx-auto mb-2" style={{ color: "var(--moss)" }} />
            <div className="font-heading text-lg" style={{ fontWeight: 500 }}>Paid in full</div>
            {inv.paid_at && <div className="text-[12px] text-[var(--ink-3)] font-mono">{fmtDate(inv.paid_at)}</div>}
          </div>
        ) : (
          <button onClick={pay} disabled={busy} className="btn-primary w-full justify-center" data-testid="portal-invoice-pay-btn">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
            {busy ? "Redirecting to Stripe…" : `Pay ${fmtMoney(inv.total, inv.currency)} securely`}
          </button>
        )}
        <div className="text-center text-[11px] text-[var(--ink-3)] mt-3">
          Payments are processed by Stripe. Your card details never touch our servers.
        </div>
      </div>
    </div>
  );
}
